import {
  biKpiGroupLines,
  kBiKpiGapPx,
  kBiKpiTileMinPx,
} from '@euler/app/components/message/widgets/bi-visualization/functions/biKpiGroupLines';
import {
  BiCustomLayout,
  BiDashboardLayout,
  BiLayoutItem,
  BiLayoutMode,
  BiPresetColumns,
  BiPresetLayout,
  BiPresetSpan,
  kBiGridColumns,
  kBiMaxItemHeight,
} from '@euler/model/bi/dashboard';
import { BiVizSpec, BiVizType } from '@euler/model/bi/viz';
import { BiCardKind, biCardKind } from './biCardKind';

/**
 * Client-owned layout geometry (spec §11.3): breakpoints, row height and
 * card heights by kind are not stored. One grid row is the unit of both
 * modes, so a preset arrangement converts to the same picture in custom.
 */

/** Below this content width every card takes a row of its own. */
export const kBiStackedBelow = 600;
/** From this content width an `auto` preset uses three columns. */
export const kBiThreeColumnsFrom = 1100;
/** One grid row, the gap below it included. */
export const kBiGridRowPx = 80;
export const kBiGridGapPx = 12;

/** Grid rows a card takes wherever its height is not stored. */
export const kBiCardKindRows: Record<BiCardKind, number> = {
  [BiCardKind.Kpi]: 3,
  [BiCardKind.Chart]: 4,
  [BiCardKind.Table]: 5,
};

/**
 * Generous estimates of a KPI group's frame and tiles. Where a KPI group
 * fits its content (phone, preset) it is measured, not estimated; these
 * only size it on the custom grid a first drag converts to, so they err
 * tall rather than clip.
 */
const kBiCardChromePx = 96;
const kBiKpiTilePx = 66;
const kBiKpiDeltaPx = 26;

/**
 * An estimate of a KPI group card's height at `width`, its tiles wrapped
 * as the view wraps them (two per row on a phone). Null for any other
 * card, or before the width is known.
 */
export function biKpiGroupPx(
  viz: BiVizSpec,
  width: number,
  /** The screen's scale, as `BiKpiGroupView` sizes its tiles with it. */
  rpx: (px: number) => number = px => px,
): number | null {
  if (viz.type !== BiVizType.KpiGroup || width <= 0) return null;
  // Inside the card's frame padding, with the view's tile and gap sizes.
  const gap = rpx(kBiKpiGapPx);
  const rows = biKpiGroupLines(
    viz.items.length,
    width - rpx(14) * 2,
    gap,
    rpx(kBiKpiTileMinPx),
  ).length;
  if (rows === 0) return null;
  const tile = rpx(kBiKpiTilePx + (viz.compare ? kBiKpiDeltaPx : 0));
  return rpx(kBiCardChromePx) + rows * tile + (rows - 1) * gap;
}

/**
 * The grid rows a card takes at `width`: its kind's, except a KPI group,
 * the fewest rows that hold it and never fewer than a KPI's.
 */
export function biCardRows(
  viz: BiVizSpec,
  width: number,
  rpx: (px: number) => number = px => px,
): number {
  const base = kBiCardKindRows[biCardKind(viz)];
  const px = biKpiGroupPx(viz, width, rpx);
  if (px === null) return base;
  return Math.max(
    kBiCardKindRows[BiCardKind.Kpi],
    Math.ceil((px + kBiGridGapPx) / kBiGridRowPx),
  );
}

/**
 * A card that is as tall as its content where its height is not stored:
 * a KPI group, whose tiles wrap and whose text the font sizes.
 */
export function biCardFits(viz: BiVizSpec): boolean {
  return viz.type === BiVizType.KpiGroup;
}

/** The pixel height of an item `rows` grid rows tall. */
export function biRowsHeight(rows: number): number {
  return rows * kBiGridRowPx - kBiGridGapPx;
}

/**
 * The fewest grid rows an item `px` tall needs. A hair over a row boundary
 * (layout rounding) does not take another row.
 */
export function biRowsFor(px: number): number {
  return Math.max(1, Math.ceil((px + kBiGridGapPx) / kBiGridRowPx - 0.01));
}

/** The narrowest a card of the custom grid is drawn before its row wraps. */
export const kBiMinCardPx = 320;

/**
 * A custom grid as drawn at a content width where some row's cards would
 * be narrower than {@link kBiMinCardPx} (four cards side by side on a
 * tablet): that row splits into even lines of equal cards, each line as
 * tall as the row, and the rows below move down. Only a clean row wraps —
 * cards starting on one line, one height, nothing beside them reaching
 * into it. The stored layout is untouched; the same items come back when
 * nothing wraps.
 */
export function biWrapNarrowRows(
  items: BiLayoutItem[],
  width: number,
): BiLayoutItem[] {
  const column = (width + kBiGridGapPx) / kBiGridColumns;
  const narrow = (item: BiLayoutItem) =>
    item.w * column - kBiGridGapPx < kBiMinCardPx;
  if (!items.some(narrow)) return items;
  // Cards per line divide the 12 columns evenly (1, 2, 3, 4 or 6), so no
  // column is left over; the most that keep each card wide enough.
  const kEven = [1, 2, 3, 4, 6];
  const wide = (perLine: number) =>
    (kBiGridColumns / perLine) * column - kBiGridGapPx >= kBiMinCardPx;
  const perLineMax = [...kEven].reverse().find(wide) ?? 1;
  const rows = new Map<number, BiLayoutItem[]>();
  for (const item of biReadingOrder(items))
    rows.set(item.y, [...(rows.get(item.y) ?? []), item]);
  const drawn: BiLayoutItem[] = [];
  let shift = 0;
  for (const [y, row] of [...rows.entries()].sort((a, b) => a[0] - b[0])) {
    const h = row[0].h;
    const clean =
      row.every(item => item.h === h) &&
      !items.some(
        other => other.y !== y && other.y < y + h && other.y + other.h > y,
      );
    const top = y + shift;
    if (!clean || !row.some(narrow)) {
      drawn.push(...row.map(item => ({ ...item, y: top })));
      continue;
    }
    const lines = Math.ceil(row.length / perLineMax);
    // As even as the lines allow: seven cards on three per line go 3, 3, 1;
    // four go 2 and 2, not 3 and 1.
    const perLine =
      kEven.find(even => even >= Math.ceil(row.length / lines)) ?? perLineMax;
    const w = kBiGridColumns / perLine;
    row.forEach((item, i) =>
      drawn.push({
        ...item,
        x: (i % perLine) * w,
        y: top + Math.floor(i / perLine) * h,
        w,
      }),
    );
    shift += (Math.ceil(row.length / perLine) - 1) * h;
  }
  return biSameGrid(drawn, items) ? items : drawn;
}

/**
 * The rows {@link biFitGridRows} inserts at each stored line: the largest
 * shortfall of a card ending on it.
 */
export function biFitLines(
  items: BiLayoutItem[],
  needs: Readonly<Record<string, number>>,
): Map<number, number> {
  const lines = new Map<number, number>();
  for (const item of items) {
    const need = needs[item.cardId];
    if (need === undefined || need <= item.h) continue;
    const line = item.y + item.h;
    lines.set(line, Math.max(lines.get(line) ?? 0, need - item.h));
  }
  return lines;
}

/**
 * The stored row a drawn row of a fitted grid stands for: the last whose
 * top, moved down by the rows inserted at or above it, is not below it.
 */
export function biStoredRow(drawn: number, lines: Map<number, number>): number {
  const inserted = (row: number) =>
    [...lines].reduce(
      (sum, [line, rows]) => (line <= row ? sum + rows : sum),
      0,
    );
  for (let row = drawn; row > 0; row--)
    if (row + inserted(row) <= drawn) return row;
  return 0;
}

/**
 * A custom grid as drawn when some cards need more rows than stored (a KPI
 * group whose tiles wrap at this width): each shortfall inserts rows at
 * that card's bottom line, so every card below moves down and every card
 * reaching the line grows with it, keeping rows level. The stored layout
 * is untouched; a wider screen draws it as stored.
 */
export function biFitGridRows(
  items: BiLayoutItem[],
  /** Rows each card needs at the width drawn, by card id. */
  needs: Readonly<Record<string, number>>,
): BiLayoutItem[] {
  const lines = biFitLines(items, needs);
  if (!lines.size) return items;
  let fitted = items;
  // Bottom line first, so a higher line is still where it was stored.
  for (const line of [...lines.keys()].sort((a, b) => b - a)) {
    const rows = lines.get(line)!;
    fitted = fitted.map(item =>
      item.y >= line
        ? { ...item, y: item.y + rows }
        : item.y + item.h >= line
        ? { ...item, h: item.h + rows }
        : item,
    );
  }
  return fitted;
}

/** Items top to bottom, then left to right. */
export function biReadingOrder(items: BiLayoutItem[]): BiLayoutItem[] {
  return [...items].sort((a, b) => a.y - b.y || a.x - b.x);
}

/** The order cards stack in on a phone, in either mode. */
export function biStackedCardIds(layout: BiDashboardLayout): string[] {
  return layout.mode === BiLayoutMode.Preset
    ? layout.order
    : biReadingOrder(layout.items).map(item => item.cardId);
}

export function biPresetColumnCount(
  columns: BiPresetColumns,
  width: number,
): 2 | 3 {
  if (columns !== 'auto') return columns;
  return width >= kBiThreeColumnsFrom ? 3 : 2;
}

export interface BiPresetRow {
  cardIds: string[];
  /** One card across the whole width. */
  full: boolean;
}

/** A preset layout flowed into rows of `columnCount` cards. */
export function biPresetRows(
  layout: BiPresetLayout,
  columnCount: number,
): BiPresetRow[] {
  const rows: BiPresetRow[] = [];
  let current: string[] = [];
  const close = () => {
    if (current.length) rows.push({ cardIds: current, full: false });
    current = [];
  };
  for (const cardId of layout.order) {
    if (layout.spans?.[cardId] === 'full') {
      close();
      rows.push({ cardIds: [cardId], full: true });
      continue;
    }
    current.push(cardId);
    if (current.length === columnCount) close();
  }
  close();
  return rows;
}

/** The grid rows a preset row takes: its tallest card's. */
export function biPresetRowRows(
  row: BiPresetRow,
  kindOf: (cardId: string) => BiCardKind,
  rowsOf?: (cardId: string) => number | null,
): number {
  // A card not loaded yet counts at its kind's height.
  return Math.max(
    ...row.cardIds.map(id => rowsOf?.(id) ?? kBiCardKindRows[kindOf(id)]),
  );
}

/**
 * The preset arrangement as it is drawn at `columnCount` columns, as items
 * on the 12-column grid: the custom layout a first drag starts from.
 */
export function biPresetToCustom(
  layout: BiPresetLayout,
  columnCount: number,
  kindOf: (cardId: string) => BiCardKind,
  /** A card's rows when it spans the row (`full`) or one column. */
  rowsOf?: (cardId: string, full: boolean) => number | null,
): BiCustomLayout {
  const items: BiLayoutItem[] = [];
  let y = 0;
  for (const row of biPresetRows(layout, columnCount)) {
    const h = biPresetRowRows(
      row,
      kindOf,
      rowsOf ? id => rowsOf(id, row.full) : undefined,
    );
    // As drawn: the row's cards share the whole width (12 divides by 1–3).
    const w = kBiGridColumns / row.cardIds.length;
    const top = y;
    row.cardIds.forEach((cardId, i) =>
      items.push({ cardId, x: i * w, y: top, w, h }),
    );
    y += h;
  }
  return { mode: BiLayoutMode.Custom, items };
}

/** Reset layout: back to an `auto` preset in the current reading order. */
export function biCustomToPreset(layout: BiCustomLayout): BiPresetLayout {
  const ordered = biReadingOrder(layout.items);
  const spans: Record<string, BiPresetSpan> = {};
  for (const item of ordered)
    if (item.w === kBiGridColumns) spans[item.cardId] = 'full';
  const preset: BiPresetLayout = {
    mode: BiLayoutMode.Preset,
    columns: 'auto',
    order: ordered.map(item => item.cardId),
  };
  if (Object.keys(spans).length) preset.spans = spans;
  return preset;
}

export function biItemsOverlap(a: BiLayoutItem, b: BiLayoutItem): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  );
}

export function biGridBottom(items: BiLayoutItem[]): number {
  return Math.max(0, ...items.map(item => item.y + item.h));
}

/** Each item, in reading order, moves below whatever it collides with. */
function pushDown(placed: BiLayoutItem, others: BiLayoutItem[]) {
  const settled = [placed];
  for (const original of biReadingOrder(others)) {
    const item = { ...original };
    for (;;) {
      const colliders = settled.filter(entry => biItemsOverlap(entry, item));
      if (!colliders.length) break;
      item.y = Math.max(...colliders.map(entry => entry.y + entry.h));
    }
    settled.push(item);
  }
  return settled;
}

/** Vertical compaction, as the server does it: each item rises while it can. */
export function biCompactGrid(items: BiLayoutItem[]): BiLayoutItem[] {
  const compacted: BiLayoutItem[] = [];
  for (const original of biReadingOrder(items)) {
    const item = { ...original };
    while (
      item.y > 0 &&
      !compacted.some(entry =>
        biItemsOverlap(entry, { ...item, y: item.y - 1 }),
      )
    )
      item.y--;
    compacted.push(item);
  }
  return biReadingOrder(compacted);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Fixes `target` (clamped into the grid), settles the rest around it. */
function settle(items: BiLayoutItem[], target: BiLayoutItem): BiLayoutItem[] {
  const w = clamp(target.w, 1, kBiGridColumns);
  const h = clamp(target.h, 1, kBiMaxItemHeight);
  const fixed: BiLayoutItem = {
    cardId: target.cardId,
    w,
    h,
    x: clamp(target.x, 0, kBiGridColumns - w),
    y: clamp(target.y, 0, Number.MAX_SAFE_INTEGER),
  };
  const others = items.filter(item => item.cardId !== target.cardId);
  return biCompactGrid(pushDown(fixed, others));
}

/** A drag: the card goes to cell (x, y); colliding cards move down. */
export function biMoveGridItem(
  items: BiLayoutItem[],
  cardId: string,
  x: number,
  y: number,
): BiLayoutItem[] {
  const item = items.find(entry => entry.cardId === cardId);
  if (!item) return items;
  return settle(items, { ...item, x, y });
}

/** A resize: the card takes w × h cells, its top-left corner fixed. */
export function biResizeGridItem(
  items: BiLayoutItem[],
  cardId: string,
  w: number,
  h: number,
): BiLayoutItem[] {
  const item = items.find(entry => entry.cardId === cardId);
  if (!item) return items;
  return settle(items, { ...item, w: Math.min(w, kBiGridColumns - item.x), h });
}

/**
 * The cards level with `cardId` (same top, same height `was`) take `h`, and
 * the grid settles below them. A card beside it that is taller or shorter
 * on purpose is left as it is.
 */
function levelRow(
  items: BiLayoutItem[],
  cardId: string,
  was: number,
  h: number,
): BiLayoutItem[] {
  const anchor = items.find(entry => entry.cardId === cardId);
  if (!anchor) return items;
  let next = items;
  for (const peer of items.filter(
    entry => entry.cardId !== cardId && entry.y === anchor.y && entry.h === was,
  )) {
    const current = next.find(entry => entry.cardId === peer.cardId)!;
    next = settle(next, { ...current, h });
  }
  return next;
}

/** The narrowest a row neighbour gets when a resize takes its room. */
const kBiMinNeighbourColumns = 2;

/**
 * Widths that share `space` columns in proportion to `widths`, each at
 * least `min`, adding up to `space` exactly (largest remainders first).
 */
function shareColumns(widths: number[], space: number, min: number): number[] {
  const total = widths.reduce((sum, w) => sum + w, 0);
  const spare = space - min * widths.length;
  const raw = widths.map(w => (spare * w) / total);
  const shares = raw.map(Math.floor);
  let left = spare - shares.reduce((sum, w) => sum + w, 0);
  const order = raw
    .map((value, index) => ({ index, rest: value - Math.floor(value) }))
    .sort((a, b) => b.rest - a.rest);
  for (const { index } of order) {
    if (left <= 0) break;
    shares[index]++;
    left--;
  }
  return shares.map(share => share + min);
}

/**
 * A resize, its row kept whole: a width change is taken from (or given
 * to) the cards to its right in the same row, which share what is left in
 * proportion, rather than one wrapping to a new row; a height change is
 * followed by the cards level with it.
 */
export function biResizeLevelItem(
  items: BiLayoutItem[],
  cardId: string,
  w: number,
  h: number,
): BiLayoutItem[] {
  const before = items.find(entry => entry.cardId === cardId);
  if (!before) return items;
  const right = items
    .filter(
      entry =>
        entry.cardId !== cardId &&
        entry.y === before.y &&
        entry.x >= before.x + before.w,
    )
    .sort((a, b) => a.x - b.x);
  let next = items;
  let width = w;
  if (right.length && w !== before.w) {
    const end = Math.max(...right.map(entry => entry.x + entry.w));
    width = Math.max(
      1,
      Math.min(w, end - before.x - kBiMinNeighbourColumns * right.length),
    );
    const widths = shareColumns(
      right.map(entry => entry.w),
      end - before.x - width,
      kBiMinNeighbourColumns,
    );
    let x = before.x + width;
    const placed = new Map(
      right.map((entry, index) => {
        const item = { ...entry, x, w: widths[index] };
        x += widths[index];
        return [entry.cardId, item];
      }),
    );
    next = items.map(entry =>
      entry.cardId === cardId
        ? { ...entry, w: width }
        : placed.get(entry.cardId) ?? entry,
    );
  }
  const resized = biResizeGridItem(next, cardId, width, h);
  const item = resized.find(entry => entry.cardId === cardId);
  return item && item.h !== before.h
    ? levelRow(resized, cardId, before.h, item.h)
    : resized;
}

/**
 * A drag, its row kept level: dropped beside cards that share one height,
 * the card takes it.
 */
export function biMoveLevelItem(
  items: BiLayoutItem[],
  cardId: string,
  x: number,
  y: number,
): BiLayoutItem[] {
  const moved = biMoveGridItem(items, cardId, x, y);
  const item = moved.find(entry => entry.cardId === cardId);
  if (!item) return moved;
  const heights = new Set(
    moved
      .filter(entry => entry.y === item.y && entry.cardId !== cardId)
      .map(entry => entry.h),
  );
  if (heights.size !== 1) return moved;
  const [h] = heights;
  return h === item.h ? moved : settle(moved, { ...item, h });
}

/** Same cards in the same cells, whatever the item order. */
export function biSameGrid(a: BiLayoutItem[], b: BiLayoutItem[]): boolean {
  if (a.length !== b.length) return false;
  const cells = new Map(
    a.map(item => [item.cardId, `${item.x},${item.y},${item.w},${item.h}`]),
  );
  return b.every(
    item =>
      cells.get(item.cardId) === `${item.x},${item.y},${item.w},${item.h}`,
  );
}
